import { ActionIcon, Tooltip } from '@mantine/core';
import { useViewportSize } from '@mantine/hooks';
import { Icon } from '@uag/react-core';
import { useState, type PointerEvent as ReactPointerEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Rnd } from 'react-rnd';

import { CopilotPanel } from 'app/modules/wallforming/components/copilot/CopilotPanel';
import {
    getCopilotDockTargetBounds,
    overlapsCopilotDockTarget
} from 'app/modules/wallforming/components/copilot/utils/copilotUtils';

import type { CopilotChatLayoutProps } from 'app/modules/wallforming/components/copilot/types/copilotTypes';

import 'app/modules/wallforming/custom-styles/customAnimationStyle.css';

const PANEL_MARGIN = 12;
const MIN_PANEL_WIDTH = 320;
const MIN_PANEL_HEIGHT = 360;

export const CopilotChatLayout = ({
    isOpen,
    isFloating,
    floatingBounds,
    onClose,
    onFloatingBoundsChange,
    onPopOut,
    onDock
}: CopilotChatLayoutProps) => {
    const { t } = useTranslation();
    const viewport = useViewportSize();
    const viewportWidth = viewport.width || window.innerWidth;
    const viewportHeight = viewport.height || window.innerHeight;
    const availableWidth = Math.max(1, viewportWidth - PANEL_MARGIN * 2);
    const availableHeight = Math.max(1, viewportHeight - PANEL_MARGIN * 2);
    const [dockTargetBounds, setDockTargetBounds] = useState<{
        left: number;
        top: number;
        width: number;
        height: number;
    } | null>(null);
    const width = Math.min(Math.max(MIN_PANEL_WIDTH, floatingBounds.width), availableWidth);
    const height = Math.min(Math.max(MIN_PANEL_HEIGHT, floatingBounds.height), availableHeight);
    const x = Math.max(PANEL_MARGIN, Math.min(floatingBounds.x, viewportWidth - width - PANEL_MARGIN));
    const y = Math.max(PANEL_MARGIN, Math.min(floatingBounds.y, viewportHeight - height - PANEL_MARGIN));

    const updateFloatingPosition = (clientX: number, clientY: number) => {
        onFloatingBoundsChange({
            ...floatingBounds,
            x: Math.max(PANEL_MARGIN, Math.min(clientX - width / 2, viewportWidth - width - PANEL_MARGIN)),
            y: Math.max(PANEL_MARGIN, Math.min(clientY - 20, viewportHeight - height - PANEL_MARGIN))
        });
    };

    const handleDockedHeaderPointerDown = (event: ReactPointerEvent<HTMLElement>) => {
        if (isFloating || event.button !== 0 || (event.target instanceof Element && event.target.closest('button'))) {
            return;
        }

        const drag = {
            pointerId: event.pointerId,
            startX: event.clientX,
            startY: event.clientY,
            hasPoppedOut: false
        };

        const finishDrag = (pointerEvent: PointerEvent) => {
            if (pointerEvent.pointerId !== drag.pointerId) {
                return;
            }
            window.removeEventListener('pointermove', moveDrag, true);
            window.removeEventListener('pointerup', finishDrag, true);
            window.removeEventListener('pointercancel', finishDrag, true);
        };
        const moveDrag = (pointerEvent: PointerEvent) => {
            if (pointerEvent.pointerId !== drag.pointerId) {
                return;
            }

            if (!drag.hasPoppedOut) {
                const distance = Math.hypot(pointerEvent.clientX - drag.startX, pointerEvent.clientY - drag.startY);
                if (distance < 6) {
                    return;
                }
                drag.hasPoppedOut = true;
                onPopOut();
            }

            pointerEvent.preventDefault();
            updateFloatingPosition(pointerEvent.clientX, pointerEvent.clientY);
        };

        window.addEventListener('pointermove', moveDrag, true);
        window.addEventListener('pointerup', finishDrag, true);
        window.addEventListener('pointercancel', finishDrag, true);
    };

    const finishFloatingDrag = (_: unknown, position: { x: number; y: number }) => {
        const target = getCopilotDockTargetBounds();
        setDockTargetBounds(null);

        if (target && overlapsCopilotDockTarget({ ...position, width, height }, target)) {
            onDock();
            return;
        }

        onFloatingBoundsChange({ ...floatingBounds, x: position.x, y: position.y });
    };

    return (
        <div
            className={isFloating ? 'pointer-events-none fixed inset-0 z-9998' : 'relative h-full min-h-0 min-w-0'}
            data-input-ui
            onKeyDown={(event) => event.stopPropagation()}
            onPointerDown={(event) => event.stopPropagation()}
            onPointerUp={(event) => event.stopPropagation()}
        >
            {isFloating && dockTargetBounds && (
                <div
                    aria-hidden="true"
                    className="pointer-events-none fixed z-9999 flex items-center justify-center rounded border-2 border-dashed border-text-accent bg-background-information-ultra-light-focus/70 text-xs font-semibold text-text-accent shadow-lg"
                    style={{
                        left: dockTargetBounds.left,
                        top: dockTargetBounds.top,
                        width: dockTargetBounds.width,
                        height: dockTargetBounds.height
                    }}
                >
                    <span
                        className="whitespace-nowrap rounded bg-background-information-ultra-light-focus! px-2 py-1 shadow"
                        style={{ transform: 'rotate(90deg)' }}
                    >
                        {t('copilotDropHere')}
                    </span>
                </div>
            )}
            <Rnd
                bounds="parent"
                cancel="button, input, textarea, select"
                disableDragging={!isFloating}
                dragHandleClassName="copilot-drag-handle"
                enableResizing={isFloating}
                enableUserSelectHack={false}
                maxHeight={isFloating ? availableHeight : undefined}
                maxWidth={isFloating ? availableWidth : undefined}
                minHeight={isFloating ? Math.min(MIN_PANEL_HEIGHT, availableHeight) : undefined}
                minWidth={isFloating ? Math.min(MIN_PANEL_WIDTH, availableWidth) : undefined}
                position={isFloating ? { x, y } : { x: 0, y: 0 }}
                resizeHandleClasses={{ bottomRight: 'copilot-resize-handle' }}
                size={isFloating ? { width, height } : { width: '100%', height: '100%' }}
                style={{ pointerEvents: 'auto' }}
                onDragStart={() => setDockTargetBounds(getCopilotDockTargetBounds())}
                onDragStop={finishFloatingDrag}
                onResizeStop={(_, __, element, ___, position) =>
                    onFloatingBoundsChange({
                        width: element.offsetWidth,
                        height: element.offsetHeight,
                        x: position.x,
                        y: position.y
                    })
                }
            >
                <section
                    aria-label={t('copilotTitle')}
                    aria-modal={isFloating ? false : undefined}
                    className={`flex h-full min-h-0 min-w-0 flex-col overflow-hidden bg-background-default text-text-default ${
                        isFloating ? 'rounded-lg border border-border-light shadow-xl' : ''
                    }`}
                    role={isFloating ? 'dialog' : undefined}
                >
                    <header
                        className={`copilot-drag-handle flex shrink-0 items-center gap-2 border-b border-border-ultra-light bg-background-gray-ultra-light px-3 py-2 ${
                            isFloating ? 'cursor-move justify-between' : 'cursor-move justify-end'
                        }`}
                        onDoubleClick={() => {
                            if (isFloating) {
                                onDock();
                            } else {
                                onPopOut();
                            }
                        }}
                        onPointerDown={handleDockedHeaderPointerDown}
                    >
                        {isFloating && (
                            <h2 className="flex min-w-0 items-center gap-2 text-sm font-semibold">
                                <Icon
                                    aria-hidden="true"
                                    className="animate-[copilot-glow_3s_ease-in-out_infinite] shrink-0 text-text-accent"
                                    icon="auto_awesome"
                                    size="lg"
                                />
                                <span className="truncate">{t('copilotTitle')}</span>
                            </h2>
                        )}
                        <div className="flex shrink-0 gap-1">
                            <Tooltip label={t(isFloating ? 'copilotDock' : 'copilotPopOut')}>
                                <ActionIcon
                                    aria-label={t(isFloating ? 'copilotDock' : 'copilotPopOut')}
                                    className="action-icon-small"
                                    variant="subtle"
                                    onClick={isFloating ? onDock : onPopOut}
                                >
                                    <Icon
                                        aria-hidden="true"
                                        icon={isFloating ? 'dock_to_right' : 'open_in_new'}
                                        size="sm"
                                    />
                                </ActionIcon>
                            </Tooltip>
                            <Tooltip className={`${!isFloating ? 'hidden' : ''}`} label={t('copilotClose')}>
                                <ActionIcon
                                    aria-label={t('copilotClose')}
                                    className="action-icon-small"
                                    variant="subtle"
                                    onClick={onClose}
                                >
                                    <Icon aria-hidden="true" icon="close" size="sm" />
                                </ActionIcon>
                            </Tooltip>
                        </div>
                    </header>
                    <div className="min-h-0 min-w-0 flex-1">
                        <CopilotPanel isOpen={isOpen} onClose={onClose} />
                    </div>
                </section>
            </Rnd>
        </div>
    );
};
